import React, { useEffect, useState } from 'react'

// Components.
import { Button, CardText, Form, FormGroup, FormFeedback, Input, Label, Card, CardBody, } from 'reactstrap';

import { validation as validationFunctions } from '../../utils';
import { defaultFormData } from '../../utils/constants/general';
import {  isEmailOnWaitList, storeWaitListUser } from '../../utils/firebase/firestore';
import VerifiedContent from '../VerifiedContent';

const defaultEmailFormData = {
    ...defaultFormData.email,
    message: 'A valid email address is required to join a waitlist.'
};

function SignupCapContent({ roleData: { id: roleId, title = '' } = {}, userId, userEmail, isUserAuthenticated, isCapped = true, isRegistrationOpen = false }) {
    const [isLoading, setIsLoading] = useState(false);
    const [isUserOnWaitlist, setIsUserOnWaitlist] = useState(false);
    const [emailFormData, setEmailFormData] = useState({ ...defaultEmailFormData });
    const emailInputId = `waitlist-email-${roleId || 'role'}`;
    const emailFeedbackId = `${emailInputId}-feedback`;

    const {
        value: emailValue,
        isTouched: isEmailTouched,
        message: emailErrorMessage
    } = emailFormData;

    const isEmailValid = emailFormData.isValid || Boolean(userEmail);

    const handleValidation = (name, value) => {
        const validation = validationFunctions.validateEmail(value);
        return { ...emailFormData, ...validation, isTouched: emailFormData.isTouched };
    }; 

    const handleChange = ({ target: { name, value }}) => {
        const validation = handleValidation(name, value);
        setEmailFormData({
            ...emailFormData,
            ...validation,
            value
        });
    };

    const handleBlur = async ({ target: {name, value }}) => {
        let validation = handleValidation(name, value);
        let isOnWaitList = true;

        try {
            setIsLoading(true);
            isOnWaitList = await isEmailOnWaitList(emailValue.trim(), roleId);
        } catch ({ code = '', cause, message: errorMessage }) {
            let message = errorMessage;
            if (code === 'auth/email-already-in-use' || cause?.code === 'auth/email-already-in-use') {
                message = 'Email address is already in use. Try another email.';
                validation = { ...validation, message, isValid: false };
            } else if (cause?.code === 'custom/username-already-in-use') {
                message = 'Username is already in use. Try signing in.';
                validation = { ...validation, message, isValid: false };
            }
        } finally {
            setIsLoading(false);

            let message = emailErrorMessage;
            if (isOnWaitList) {
                message = 'You are already on this waitlist.';
            }
            
            setEmailFormData({
                ...emailFormData,
                ...validation,
                message,
                isValid: validation.isValid && !isOnWaitList,
                isTouched: true
            });
        }
    };

    const handleSubmit = async event => {
        event.preventDefault();
        let isOnWaitList = true;

        try {
            setIsLoading(true);
            isOnWaitList = await isEmailOnWaitList(userEmail || emailValue.trim(), roleId);
        } catch (error) {
            console.log(error);            
        } finally {
            setIsLoading(false);

            let validation = handleValidation('email', userEmail || emailValue);

            if (isEmailValid && !isOnWaitList) {
                storeWaitListUser(userEmail || emailValue.trim(), roleId)
                setIsUserOnWaitlist(true);
                setEmailFormData({ ...defaultEmailFormData });
            } else {
                let message = emailErrorMessage;
                if (isOnWaitList) {
                    message = 'You are already on this waitlist.';
                }
                
                setEmailFormData({
                    ...emailFormData,
                    ...validation,
                    message,
                    isValid: validation.isValid && !isOnWaitList,
                    isTouched: true
                });
            }
        }
    }

    useEffect(() => {
      if (!userEmail || !roleId) return;

      const fetchIsUserOnWaitlist = async () => {
        try {
            const isOnWaitList = await isEmailOnWaitList(userEmail, roleId);
            setIsUserOnWaitlist(isOnWaitList);
        } catch (error) {
            console.log(error);
        }
      };

      fetchIsUserOnWaitlist();
    }, [userEmail, roleId]);
    


  return (
    <div className="">
        <div className="text-center fs-5">
            <Card className="border-primary-orange-subtle shadow-xs surface-elevated">
                <CardBody className="panel-card-body py-4">
                    <div className="d-flex flex-column align-items-center gap-2">
                        <div className="mt-1">
                            <div className="text-secondary text-uppercase anton-regular">
                                {isRegistrationOpen ? 'Signup cap reached for' :  'Registration is closed for'}
                            </div>
                            <div className="display-5 my-2 text-light fw-normal sigmar-regular lh-1" style={{ letterSpacing: '0.15rem' }}>
                                {title}
                            </div>
                        </div>

                        {isUserOnWaitlist && (
                            <CardText className="text-secondary mb-0">
                                You are on this waitlist and will receive an email when spots open.
                            </CardText>
                        )}

                        {!isUserOnWaitlist && (isCapped || !isRegistrationOpen) && (
                            <>
                                <CardText className="text-secondary mb-0">
                                    Join the waitlist to receive an email when spots open.
                                </CardText>
            
                                <Form 
                                    noValidate
                                    id="signin-form"
                                    onSubmit={handleSubmit}
                                    className="form-shell d-flex flex-column justify-content-around"
                                >
                                    {!isUserAuthenticated && (
                                        <FormGroup floating className="no-fricken-bottom-margin">
                                            <Input
                                                required
                                                type="email"
                                                name="email"
                                                id={emailInputId}
                                                placeholder="Email address"
                                                value={emailValue}
                                                invalid={isEmailTouched && !isEmailValid}
                                                aria-required="true"
                                                aria-invalid={isEmailTouched && !isEmailValid}
                                                aria-describedby={emailFeedbackId}
                                                tabIndex={2}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                className="input-tracked text-light text-light-emphasis bg-darker border-secondary-subtle rounded-2"
                                            />
                                                <Label for={emailInputId} className="text-secondary beta-form-label fw-semibold">Email Address</Label>
                                                <FormFeedback id={emailFeedbackId} aria-live="polite" className={`small mt-1 d-block ${isEmailTouched && !isEmailValid ? 'visible' : 'invisible'}`}>
                                                    {emailErrorMessage}
                                                </FormFeedback>
                                        </FormGroup>
                                    )}
            
                                    <Button
                                        id={`waitlist-submit-${roleId || 'role'}`}
                                        type='submit'
                                        color='primary'
                                        size="lg"
                                        tabIndex={3}
                                        aria-busy={isLoading}
                                        aria-live="polite"
                                        disabled={isLoading || !isEmailValid}
                                        className="align-self-center mt-2 fw-semibold border-2 px-4"
                                    >
                                        {isLoading ? 'Joining...' : `Join ${title} Waitlist`}
                                    </Button>

                                    <CardText className="fs-smaller text-secondary mb-0">
                                        If notified, you&apos;ll have 48 hours to confirm.
                                    </CardText>
                                </Form>
                            </>
                        )}

                        <p className="mb-0 text-secondary anton-regular opacity-75">
                            <strong>NOTE: </strong>Check the other roles while this one is unavailable.
                        </p>
                    </div>
                </CardBody>
            </Card>

            <div className="my-3">
                <VerifiedContent isBetaRoleCardShown={false} />
            </div>
        </div>
    </div>
  )
}

export default SignupCapContent
